// Web implementation of the popup component.

import { HoverView } from 'component/basics';
import { colorSurfacePrimary } from 'component/color';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ScrollView, StyleSheet, View } from 'react-native';
import { FocusLoopView } from 'system/focustrap';

export function DocumentLevelComponent({ children }) {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        setContainer(container);
        return () => {
            document.body.removeChild(container);
        };
    }, []);

    if (!container) return null;

    return ReactDOM.createPortal(children, container);
}

let global_active_popup_closer: (() => void) | null = null;

export function closeActivePopup() {
    if (global_active_popup_closer) {
        global_active_popup_closer();
    }
}

export function PopupSelector({ value, items, onSelect, textStyle = {}, paddingVertical = 8 }) {
    const [hover, setHover] = useState(false);
    return (
        <View style={{ marginHorizontal: 0, marginVertical: 0 }}>
            <select
                onChange={(e) => onSelect(e.target.value)}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                    ...textStyle,
                    backgroundColor: 'white',
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingTop: paddingVertical,
                    paddingBottom: paddingVertical,
                    borderWidth: 1,
                    WebkitAppearance: 'none',
                    borderRadius: 8,
                    flex: 1,
                    borderColor: hover ? '#999' : '#ddd',
                }}
                value={value}
            >
                {items.map((item) => (
                    <option key={item.key} value={item.key}>
                        {item.label}
                    </option>
                ))}
            </select>
        </View>
    );
}

let global_clickTargetRef: React.RefObject<HTMLDivElement> | null = null;
let global_popupRef: React.RefObject<HTMLDivElement> | null = null;
let global_popup_align_right = false;
let global_popup_align_top = false;
let global_popup_matchWidth = false;

export function getPopupRef() {
    return global_popupRef;
}

function global_layoutPopup() {
    if (!global_clickTargetRef || !global_popupRef) {
        return;
    }
    if (!global_popupRef.current || !global_clickTargetRef.current) {
        requestAnimationFrame(global_layoutPopup);
        return;
    }

    const node = global_popupRef.current;
    const rect = global_clickTargetRef.current.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const nodeWidth = nodeRect.width;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (rect.top > windowHeight / 2) {
        if (global_popup_align_top) {
            node.style.top = '';
            node.style.bottom = windowHeight - rect.top - nodeRect.height - 24 + 'px';
            node.style.maxHeight = rect.top - 16 + 'px';
        } else {
            node.style.top = '';
            node.style.bottom = windowHeight - rect.top + 'px';
            node.style.maxHeight = rect.top - 16 + 'px';
        }
    } else {
        node.style.top = rect.bottom + 'px';
        node.style.bottom = '';
        node.style.maxHeight = windowHeight - rect.bottom - 16 + 'px';
    }
    if (global_popup_matchWidth) {
        node.style.left = rect.left + 'px';
        node.style.right = '';
        node.style.width = rect.width + 'px';
    } else if (rect.left > windowWidth / 2 || (global_popup_align_right && rect.right > nodeWidth)) {
        node.style.right = windowWidth - rect.right + 'px';
        node.style.left = '';
        node.style.maxWidth = rect.right - 16 + 'px';
    } else {
        node.style.left = rect.left + 'px';
        node.style.right = '';
        node.style.maxWidth = windowWidth - rect.left - 16 + 'px';
    }
    requestAnimationFrame(global_layoutPopup);
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function PopupBase({
    popupContent,
    popupEnabled = true,
    testID,
    popupStyle = null,
    matchWidth = false,
    alignTop = false,
    alignRight = false,
    onPress = () => {},
    setHover = () => {},
    setShown = (b: boolean) => {},
    children,
}) {
    const s = PopupButtonStyle;
    const [shown, setLocalShown] = useState(false);
    const popupRef = React.useRef<HTMLDivElement>(null);
    const clickTargetRef = React.useRef<HTMLDivElement>(null);

    const closePopup = useCallback(() => {
        setLocalShown(false);
        setShown(false);
    }, []);

    useEffect(() => {
        if (shown) {
            global_active_popup_closer = closePopup;
        } else if (global_active_popup_closer == closePopup) {
            global_active_popup_closer = null;
        }
        return () => {
            if (global_active_popup_closer == closePopup) {
                global_active_popup_closer = null;
            }
        };
    }, [shown]);

    function handleClickOutside(event: MouseEvent) {
        if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
            global_clickTargetRef = null;
            global_popupRef = null;
            setLocalShown(false);
            setShown(false);
        }
    }

    const onClickShow = useCallback(() => {
        if (popupEnabled) {
            setLocalShown(!shown);
            setShown(!shown);
        }
        onPress();
    }, [shown, popupEnabled, onPress]);

    useEffect(() => {
        if (shown) {
            global_clickTargetRef = clickTargetRef;
            global_popupRef = popupRef;
            global_popup_align_right = alignRight;
            global_popup_align_top = alignTop;
            global_popup_matchWidth = matchWidth;
            global_layoutPopup();
        } else if (global_popupRef == popupRef) {
            global_clickTargetRef = null;
            global_popupRef = null;
        }
    }, [shown]);

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <View style={s.frame}>
            <View ref={clickTargetRef as any as React.RefObject<View>}>
                <HoverView onPress={onClickShow} setHover={setHover} testID={testID}>
                    {children}
                </HoverView>
            </View>
            {shown ? (
                <DocumentLevelComponent>
                    <ScrollView
                        ref={popupRef as any as React.RefObject<ScrollView>}
                        style={[s.basicPopup, popupStyle]}
                        testID='popup-content'
                    >
                        {shown ? (
                            <FocusLoopView index={1} onCloseLoop={closePopup}>
                                {popupContent({ onClose: closePopup })}
                            </FocusLoopView>
                        ) : null}
                    </ScrollView>
                </DocumentLevelComponent>
            ) : null}
        </View>
    );
}

const PopupButtonStyle = StyleSheet.create({
    frame: {
        position: 'relative',
    },
    basicPopup: {
        position: 'absolute',
        zIndex: 1000,
        backgroundColor: colorSurfacePrimary,
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        borderRadius: 8,
        padding: 20,
    },
    up: {
        bottom: 0,
    },
    down: {
        top: 0,
    },
    left: {
        right: 0,
    },
    right: {
        left: 0,
    },
});

export function PopupIcon({ iconFamily, iconName, label = null }) {
    return <View>{React.createElement(iconFamily, { name: iconName, size: 24, color: 'white' })}</View>;
}
